import { useEffect, useState } from 'react';
import { supabase, UserActivity } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import { Calendar, UserPlus, Award, MessageCircle, MapPin, Clock } from 'lucide-react';

interface ActivityFeedProps {
  userId?: string; // If not provided, shows current user's activity
  limit?: number;
}

export default function ActivityFeed({ userId, limit = 20 }: ActivityFeedProps) {
  const { currentUser } = useEmberStore();
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivities();
  }, [userId, currentUser]);

  async function loadActivities() {
    try {
      const targetUserId = userId || currentUser?.id;
      if (!targetUserId) return;

      const { data, error } = await supabase.rpc('get_user_activity_feed', {
        user_uuid: targetUserId,
        limit_count: limit
      });

      if (error) throw error;
      if (data) setActivities(data);
    } catch (err) {
      console.error('Error loading activity feed:', err);
    } finally {
      setLoading(false);
    }
  }

  function formatActivityTime(dateString: string) {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return date.toLocaleDateString();
  }

  function getActivityIcon(activityType: string) {
    switch (activityType) {
      case 'badge_earned':
        return <Award size={16} className="text-yellow-500" />;
      case 'checkin':
        return <MessageCircle size={16} className="text-green-500" />;
      case 'event_created':
        return <Calendar size={16} className="text-blue-500" />;
      case 'event_attended':
        return <MapPin size={16} className="text-purple-500" />;
      case 'connection_made':
        return <UserPlus size={16} className="text-orange-500" />;
      default:
        return <Clock size={16} className="text-gray-500" />;
    }
  }

  function getActivityMessage(activity: UserActivity) {
    const data = activity.activity_data;
    
    switch (activity.activity_type) {
      case 'badge_earned':
        return `Earned the "${data.badge_name}" badge! ${data.badge_icon}`;
      case 'checkin':
        return `Checked in with ${data.tobacco_name || 'a tobacco'}`;
      case 'event_created':
        return `Created event "${data.event_title}"`;
      case 'event_attended':
        return `Attended "${data.event_title}"`;
      case 'connection_made':
        return `Connected with ${data.other_username}`;
      default:
        return 'Activity recorded';
    }
  }

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-gray-800 rounded-lg p-3 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-gray-700 rounded" />
              <div className="flex-1">
                <div className="h-4 bg-gray-700 rounded w-3/4 mb-1" />
                <div className="h-3 bg-gray-700 rounded w-1/4" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Clock size={32} className="mx-auto mb-2 opacity-50" />
        <p>No recent activity</p>
        <p className="text-sm">Start checking in, creating events, and connecting to see your activity here!</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {activities.map((activity, index) => (
        <div 
          key={`${activity.activity_type}-${activity.created_at}-${index}`}
          className="bg-gray-800 rounded-lg p-3 border border-gray-700 hover:border-gray-600 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              {getActivityIcon(activity.activity_type)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-200">
                {getActivityMessage(activity)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {formatActivityTime(activity.created_at)}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Compact activity feed for sidebars
export function CompactActivityFeed({ userId, limit = 5 }: ActivityFeedProps) {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">
        Recent Activity
      </h4>
      <ActivityFeed userId={userId} limit={limit} />
    </div>
  );
}
